import React, {useMemo} from "react";
import {Button, Hour, Icon, Minute, TimeSelector} from "peggirkit";
import {CalendarDaysIcon} from "@heroicons/react/16/solid";
import {dateTimeFormatPattern, formatDateTime} from "@/utils/DateUtils";
import dayjs, {Dayjs} from "dayjs";
import {SetStateFn} from "@/utils/SetStateUtils";
import {PickDateProps} from "@widgets/employees/editor/timeEntries/edit/EditTimeEntry";
import {StopCircleIcon} from "@heroicons/react/20/solid";
import {FormattedMessage} from "react-intl";

type Props = {
    label: string,
    pickDateLabel: string,
    value: string | null,
    onChange: (newValue: string | null) => void,
    setPickDateModal: SetStateFn<PickDateProps>,
    setShowEditModal: SetStateFn<boolean>,
};

const EditTimeInput = ({label, pickDateLabel, value, onChange, setPickDateModal, setShowEditModal}: Props) => {
    const parsedDate: Dayjs = useMemo(() => dayjs(value), [value]);
    const selectedHour = parsedDate.hour();
    const selectedMinute = parsedDate.minute();

    if (value === null) {
        return (
            <Button
                color={"danger"}
                buttonAction={{
                    as: "button",
                    actionProps: {
                        onClick: (e) => {
                            e.preventDefault();
                            onChange(dayjs(new Date()).format(dateTimeFormatPattern))
                        }
                    },
                }}
                iconBefore={<Icon icon={StopCircleIcon}/>}
            >
                <FormattedMessage id={"clockOut"}/>
            </Button>
        );
    }

    return (
        <div className={"w-full flex flex-col gap-3 items-start justify-start"}>
            <TimeSelector
                hour={selectedHour as Hour}
                setHour={hour => onChange(parsedDate.set("hour", hour).format(dateTimeFormatPattern))}
                minute={selectedMinute as Minute}
                setMinute={minute => onChange(parsedDate.set("minute", minute).format(dateTimeFormatPattern))}
                timeLabel={label}
                containerClassName={"w-full"}
                enabled={true}
            />

            <div className={"w-full flex flex-row items-center justify-start gap-4"}>
                <Button
                    color={"secondary"}
                    buttonAction={{
                        as: "button",
                        actionProps: {
                            onClick: (e) => {
                                e.preventDefault();

                                if (value === null) {
                                    return;
                                }

                                setPickDateModal({
                                    show: true,
                                    defaultDay: new Date(value),
                                    onContinue: (selectedDate: Date | undefined) => {
                                        if (selectedDate !== undefined) {
                                            const parsed = dayjs(selectedDate)
                                            const newDatetime = parsedDate
                                                .set("year", parsed.year())
                                                .set("month", parsed.month())
                                                .set("date", parsed.date())
                                                .format(dateTimeFormatPattern);
                                            onChange(newDatetime);
                                        }
                                    },
                                })
                                setShowEditModal(false);
                            }
                        },
                    }}
                    size={"xs"}
                    iconBefore={<Icon icon={CalendarDaysIcon}/>}
                >
                    {pickDateLabel}
                </Button>

                <time className={"text-xs text-gray-600 dark:text-gray-300"}>
                    {formatDateTime((value))}
                </time>
            </div>
        </div>
    );
};

export default EditTimeInput;